import { Switch } from '@odekoteam/doppio';
import { useState } from 'react';
import { UPDATE_LOCATIONS_INCLUDE_FINANCIALS_ON_DELIVERY_SHEET } from 'src/graphql/requests/updateLocationsIncludeFinancialsOnDeliverySheet';
import { GUpdateLocationsIncludeFinancialsOnDeliverySheetMutation } from 'src/graphql/types/generated';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { routingLocationsSelector } from 'src/state/selectors/locations';
import { updateRoutingLocations } from 'src/state/slices/locations';
import { Flex } from 'theme-ui';
import { useMutation } from 'urql';

export const includeFinancialsSwitchId = (locationId: string): string =>
  `include-financials-switch-${locationId}`;

// Whether this location's printed delivery sheets show prices and totals. Only
// meaningful while the location prints sheets, so it is disabled (showing its
// stored value) when Print Delivery Sheet is off. Both values are DERIVED from
// the routing-locations slice like PrintDeliverySheetSwitch, so flipping the
// print switch or a bulk edit enables/updates this one without a reload.
const IncludeFinancialsSwitch = ({
  locationId,
  value,
  printDeliverySheetEnabled,
}: {
  locationId: string;
  value?: boolean;
  printDeliverySheetEnabled?: boolean;
}): JSX.Element => {
  const routingLocations = useAppSelector(routingLocationsSelector);
  const dispatch = useAppDispatch();
  const locationInState = routingLocations.find(
    (location) => location.locationId === locationId,
  );
  // Default on: a location the server has not reported yet shows financials.
  const isChecked = Boolean(
    locationInState?.includeFinancialsOnDeliverySheet ?? value ?? true,
  );
  const isPrinting = Boolean(
    locationInState?.printDeliverySheetEnabled ?? printDeliverySheetEnabled,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [, updateLocationsIncludeFinancials] =
    useMutation<GUpdateLocationsIncludeFinancialsOnDeliverySheetMutation>(
      UPDATE_LOCATIONS_INCLUDE_FINANCIALS_ON_DELIVERY_SHEET,
    );

  const setFlag = (next: boolean): void => {
    dispatch(
      updateRoutingLocations([
        { locationId, includeFinancialsOnDeliverySheet: next },
      ]),
    );
  };

  const handleSwitch = (newValue: boolean): void => {
    if (isSaving || !isPrinting) return;
    const previous = isChecked;
    // optimistic + debounce
    setFlag(newValue);
    setIsSaving(true);
    updateLocationsIncludeFinancials({
      input: {
        locationIds: [locationId],
        includeFinancialsOnDeliverySheet: newValue,
      },
    })
      .then((result) => {
        const confirmed =
          result.data?.updateLocationsIncludeFinancialsOnDeliverySheet
            ?.locations[0]?.includeFinancialsOnDeliverySheet;
        // Revert on error or an incomplete payload; otherwise reconcile with
        // the server value.
        const failed = Boolean(result.error) || confirmed === undefined;
        setFlag(failed ? previous : confirmed);
        setIsSaving(false);
      })
      .catch(() => {
        setFlag(previous);
        setIsSaving(false);
      });
  };

  return (
    <Flex>
      <Switch
        id={includeFinancialsSwitchId(locationId)}
        accessibilityLabel="include financials on delivery sheet toggle"
        onCheckedChange={handleSwitch}
        size="default"
        isChecked={isChecked}
        isDisabled={!isPrinting}
      />
      <span sx={{ marginLeft: '12px', variant: 'text.paragraphLgDefault' }}>
        {isChecked ? 'On' : 'Off'}
      </span>
    </Flex>
  );
};

export default IncludeFinancialsSwitch;
